/** The pieces the full-screen panels need on top of the theme kit.
 *
 * The theme owns the material vocabulary (`Panel`, `Frame`, `Button`, `Slot`,
 * `Divider`, `StatLine`, `Icon`) and the atlas; this adds only what a
 * full-screen panel needs and the adventure shell does not: its own stylesheet,
 * a modal, an element measurer, and a sprite drawn at an exact whole scale
 * rather than fitted to a square box.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, ReactNode, RefObject } from "react";
import { Button, Divider, Frame, Icon, Panel, Slot, StatLine, cx } from "../theme/components.js";
import { spriteStyle } from "../theme/atlas.js";
import { useAtlas } from "../hooks/useAtlas.js";
import { SCREENS_CSS, SCREENS_STYLE_ID } from "./styles.js";
import { StyleLayer, useStyleSheet } from "../theme/styleSheet.js";

/* Re-exported for the screens in this folder only. `screens/index.ts`
   deliberately does NOT forward these on: `@game/ui`'s barrel already exports
   them from `theme/`, and a second star-export of the same name resolves to
   nothing at all rather than to an error. */
export { Button, Divider, Frame, Icon, Panel, Slot, StatLine, cx };

// ------------------------------------------------------------------ styles

/** Mounts the screens' stylesheet once, however many screens are open. */
export function ScreenStyles(): null {
  useStyleSheet(SCREENS_STYLE_ID, SCREENS_CSS, StyleLayer.screens);
  return null;
}

// ------------------------------------------------------------------ sprites

export interface ScaledSpriteProps {
  readonly atlasKey: string | null;
  /** Whole-number magnification. 1 draws the frame at source size. */
  readonly scale: number;
  readonly className?: string;
  readonly style?: CSSProperties;
  readonly title?: string;
}

/**
 * One frame at an exact whole scale, keeping its own aspect.
 *
 * `Icon` fits a frame into a square box, which is right for icons and wrong for
 * a 250x312 dwelling or a 1280x720 backdrop. Both go through the theme's
 * `spriteStyle`, so the sample grid maths has one implementation.
 */
export function ScaledSprite(props: ScaledSpriteProps): JSX.Element {
  const atlas = useAtlas();
  const frame = props.atlasKey === null ? null : atlas.frame(props.atlasKey);
  const scale = Math.max(1, Math.floor(props.scale));
  const style = useMemo<CSSProperties>(() => {
    if (frame === null || props.atlasKey === null) return { ...props.style };
    const target = Math.max(frame.w, frame.h) * scale;
    return {
      ...(spriteStyle(atlas, props.atlasKey, target) as CSSProperties),
      ...props.style,
    };
  }, [atlas, frame, props.atlasKey, props.style, scale]);

  return (
    <span
      className={cx("hm-icon", frame === null ? "hm-icon--missing" : "hm-icon--up", props.className)}
      style={style}
      title={props.title}
      role="presentation"
    />
  );
}

/** `Icon`, but tolerant of a key the content could not resolve. A hatched
 *  swatch is a bug you notice; an empty gap in a panel is one you do not. */
export function MaybeIcon(props: {
  readonly atlasKey: string | null;
  readonly size: number;
  /** Box height, for a socket that is not square. Defaults to `size`. */
  readonly boxHeight?: number;
  readonly title?: string;
  readonly className?: string;
  readonly style?: CSSProperties;
}): JSX.Element {
  if (props.atlasKey === null) {
    const h = props.boxHeight ?? props.size;
    return (
      <span
        className={cx("hm-icon", "hm-icon--missing", props.className)}
        style={{ width: `${String(props.size)}px`, height: `${String(h)}px`, ...props.style }}
        title={props.title}
      />
    );
  }
  return (
    <Icon
      atlasKey={props.atlasKey}
      size={props.size}
      {...(props.boxHeight === undefined ? {} : { boxHeight: props.boxHeight })}
      {...(props.title === undefined ? {} : { title: props.title })}
      {...(props.className === undefined ? {} : { className: props.className })}
      {...(props.style === undefined ? {} : { style: props.style })}
    />
  );
}

/** The drawn size of a frame at a whole scale, for laying a hit box out. */
export function useSpriteSize(
  atlasKey: string | null, scale: number,
): { readonly w: number; readonly h: number } | null {
  const atlas = useAtlas();
  const frame = atlasKey === null ? null : atlas.frame(atlasKey);
  if (frame === null) return null;
  const s = Math.max(1, Math.floor(scale));
  return { w: frame.w * s, h: frame.h * s };
}

// ----------------------------------------------------------------- measuring

export interface BoxSize { readonly w: number; readonly h: number }

/**
 * The live content box of an element.
 *
 * The town screen needs it to pick a *whole* scale for the 1280x720 painting,
 * and a whole scale cannot be chosen from a media query. `ResizeObserver`
 * rather than a window listener, because the panel is laid out inside a flex
 * column whose height does not track the window.
 */
export function useMeasure<T extends HTMLElement>(): readonly [RefObject<T>, BoxSize] {
  const ref = useRef<T>(null);
  const [size, setSize] = useState<BoxSize>({ w: 0, h: 0 });
  useEffect(() => {
    const el = ref.current;
    if (el === null || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      const box = entries[0]?.contentRect;
      if (box === undefined) return;
      setSize({ w: Math.floor(box.width), h: Math.floor(box.height) });
    });
    ro.observe(el);
    return () => { ro.disconnect(); };
  }, []);
  return [ref, size];
}

// ------------------------------------------------------------------ dialog

export interface ScreenDialogProps {
  readonly title: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
  readonly width?: number;
}

/**
 * A modal over the screen that opened it.
 *
 * Escape closes it; clicking the scrim does not, because a mis-click that
 * discards a half-filled recruit slider is a bug rather than a convenience.
 *
 * ## Escape has to be a document listener, not an `onKeyDown`
 *
 * It was `onKeyDown` on the dialog div, and that div never takes focus, so a
 * key event never reached it and the comment above this one was false for every
 * dialog opened with the mouse - which is all of them. Measured in the running
 * app: after clicking a FOR SALE socket on the town screen,
 * `document.activeElement.className` is `hs-tslot` - the button that opened the
 * dialog, still focused, outside the dialog - and Escape left the scrim up.
 *
 * Two halves, and both are needed. The listener is on `document` in the
 * CAPTURE phase, so it sees the key wherever focus happens to be and gets it
 * *before* the adventure shell's own Escape handler, which would otherwise
 * close the screen underneath at the same time; `stopPropagation` is what makes
 * Escape close exactly the topmost thing. And the dialog takes focus on mount
 * (hence `tabIndex={-1}`), so Tab starts inside the modal rather than in the
 * page behind it, and focus goes back where it came from on close.
 */
export function ScreenDialog(props: ScreenDialogProps): JSX.Element {
  const { onClose } = props;
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      e.preventDefault();
      onClose();
    };
    document.addEventListener("keydown", onKey, true);
    return () => { document.removeEventListener("keydown", onKey, true); };
  }, [onClose]);

  useEffect(() => {
    const returnTo = document.activeElement;
    ref.current?.focus();
    return () => {
      if (returnTo instanceof HTMLElement && returnTo.isConnected) returnTo.focus();
    };
  }, []);

  return (
    <div className="hs-scrim" role="presentation">
      <div
        ref={ref}
        className="hs-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={props.title}
        tabIndex={-1}
        style={props.width === undefined ? undefined : { width: `${String(props.width)}px` }}
      >
        <div className="hs-dialog__title">{props.title}</div>
        <div className="hs-gilt-rule" />
        <div className="hs-dialog__body hs-scroll">{props.children}</div>
        <div className="hs-dialog__foot">
          {props.footer}
          <button type="button" className="hs-btn hs-dialog__close" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
